import type {
  ImportedBrepRef,
  ImportedPartBodyBrepBinding,
  ImportedTopologyRef,
  ImportedTopologySource,
} from '../loaders/types'

/**
 * 导入拓扑 hydration 诊断日志所需的最小文件信息。
 */
export interface ImportedTopologyHydrationDiagnosticFile {
  /** 当前正在补齐拓扑的 GLB 文件 ID。 */
  fileId: string
  /** 当前 GLB 文件 UUID，缺失时仅输出 fileId。 */
  uuid?: string
}

/**
 * 导入拓扑 hydration 诊断输入。
 */
export interface ImportedTopologyHydrationDiagnosticInput {
  /** 当前正在补齐拓扑的 GLB 文件。 */
  file: ImportedTopologyHydrationDiagnosticFile
  /** 当前导入模型的拓扑源数据。 */
  topologySource: ImportedTopologySource
  /** 当前 body 绑定。 */
  binding: ImportedPartBodyBrepBinding
  /** 进入当前日志路径的原因。 */
  reason: string
  /** 已解析后的 topo sidecar 文件 ID。 */
  topologySidecarFileId?: string
  /** 已解析后的 BRep sidecar 文件 ID。 */
  brepSidecarFileId?: string
}

/**
 * 读取可输出的非空字符串。
 *
 * @param value 候选字符串。
 * @returns 去除首尾空白后的字符串。
 */
function readDiagnosticText(value: string | undefined): string | undefined {
  const trimmedValue = value?.trim()
  return trimmedValue && trimmedValue.length > 0 ? trimmedValue : undefined
}

/**
 * 格式化 sidecar 引用。
 *
 * @param ref sidecar 引用。
 * @param resolvedFileId 已解析文件 ID。
 * @returns 可定位到文件和 entry 的引用文本。
 */
function formatImportedSidecarRef(
  ref: ImportedBrepRef | ImportedTopologyRef | undefined,
  resolvedFileId: string | undefined,
): string {
  if (!ref) {
    return '无'
  }

  const refParts = [
    resolvedFileId ? `file=${resolvedFileId}` : undefined,
    `uri=${ref.uri}`,
    `key=${ref.key}`,
    'sha256' in ref && ref.sha256 ? `sha256=${ref.sha256}` : undefined,
    typeof ref.byteLength === 'number' ? `byteLength=${ref.byteLength}` : undefined,
    'encoding' in ref && ref.encoding ? `encoding=${ref.encoding}` : undefined,
  ].filter((part): part is string => Boolean(part))

  return refParts.join(',')
}

/**
 * 解析 body 的可读名称。
 *
 * @param topologySource 当前拓扑源。
 * @param binding 当前 body 绑定。
 * @returns body 名称。
 */
function resolveImportedTopologyBodyName(
  topologySource: ImportedTopologySource,
  binding: ImportedPartBodyBrepBinding,
): string | undefined {
  const bodyName = binding.bodyId
    ? readDiagnosticText(topologySource.bodyEntries.find((entry) => entry.bodyId === binding.bodyId)?.label)
    : undefined
  if (bodyName) {
    return bodyName
  }

  return readDiagnosticText(topologySource.meshCatalog[binding.meshId]?.objectName)
}

/**
 * 拼接诊断项。
 *
 * @param entries 诊断键值对。
 * @returns 单行诊断文本。
 */
function formatDiagnosticEntries(entries: Array<[string, string | number | undefined]>): string {
  return entries
    .filter((entry): entry is [string, string | number] => entry[1] !== undefined && entry[1] !== '')
    .map(([key, value]) => `${key}=${value}`)
    .join('; ')
}

/**
 * 格式化导入拓扑 hydration 诊断文本。
 *
 * 设计说明：
 * 1. topo sidecar 是 Feature 交互拓扑的唯一来源，BRep sidecar 只负责导出 Shape 注册；
 * 2. 诊断日志必须直接定位到实体、mesh、sidecar 引用和失败原因，避免调试时再从场景树反查；
 * 3. 该函数只做纯格式化，不读取文件、不修改拓扑源，保证可复用且不会影响加载性能。
 *
 * @param input 诊断输入。
 * @returns 可直接输出到控制台的单行诊断文本。
 */
export function formatImportedTopologyHydrationDiagnostic(
  input: ImportedTopologyHydrationDiagnosticInput,
): string {
  const meshMeta = input.topologySource.meshCatalog[input.binding.meshId]
  const bodyName = resolveImportedTopologyBodyName(input.topologySource, input.binding)

  return formatDiagnosticEntries([
    ['reason', input.reason],
    ['fileId', input.file.fileId],
    ['fileUuid', input.file.uuid],
    ['bodyBindingId', input.binding.bodyBindingId],
    ['bodyId', input.binding.bodyId],
    ['bodyName', bodyName],
    ['meshId', input.binding.meshId],
    ['meshName', readDiagnosticText(meshMeta?.objectName)],
    ['primitiveIds', input.binding.primitiveIds.join(',')],
    ['primitiveIndices', input.binding.primitiveIndices.join(',')],
    ['sourceLevel', input.binding.sourceLevel],
    ['topologyRef', formatImportedSidecarRef(input.binding.topologyRef, input.topologySidecarFileId)],
    ['brepRef', formatImportedSidecarRef(input.binding.brepRef, input.brepSidecarFileId)],
  ])
}
